import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { Job } from '../generated/gql-types';
import { formatInJobTimezone } from './jobTimezone.util';

/**
 * Stored app date answers: unix epoch in milliseconds (e.g. from `dayjs(...).valueOf()`).
 * No legacy seconds conversion — the API/DB use ms only.
 */
export function parseStoredEpochMs(
  value: string | number | null | undefined,
): number {
  if (value === '' || value === null || value === undefined) {
    return NaN;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return NaN;
  }
  return n;
}

/** Same as {@link parseStoredEpochMs} but empty or invalid input becomes `0` (for pickers / display). */
export function parseStoredEpochMsOrZero(
  value: string | number | null | undefined,
): number {
  const ms = parseStoredEpochMs(value);
  return Number.isFinite(ms) ? ms : 0;
}

export function dayjsFromStoredEpochMs(
  value: string | number | null | undefined,
): Dayjs {
  return dayjs(parseStoredEpochMs(value));
}

/**
 * Convert start datetime(unix timestamp) and enddatetime(unix timestamp) to a readable format 09:00 - 15:00
 * @param startDateTime
 * @param endDateTime
 * @returns
 */
export const convertDateTimeToReadableFormat = (
  startDateTime: string,
  endDateTime: string,
  format: 'HH:mm' | 'HH:mm - HH:mm',
  jobOrTimezone?: Pick<Job, 'timezone'> | string | null,
) => {
  const start = formatInJobTimezone(startDateTime, jobOrTimezone, 'HH:mm');
  const end = formatInJobTimezone(endDateTime, jobOrTimezone, 'HH:mm');
  if (format === 'HH:mm') {
    return `${start} – ${end}`;
  }
  const hours = computeShiftDurationHoursFromUnixMs(startDateTime, endDateTime);
  return `${start} – ${end} (${formatDurationHours(hours)} hrs)`;
};

/** Overnight-aware span between two clock times on the same logical shift day. */
export function computeShiftDurationHours(start: Dayjs, end: Dayjs): number {
  let hours = end.diff(start, 'hour', true);
  if (hours < 0) {
    hours = end.add(1, 'day').diff(start, 'hour', true);
  }
  return hours;
}

export function computeShiftDurationHoursFromUnixMs(
  startDateTime: string | number,
  endDateTime: string | number,
): number {
  const start = dayjs(parseStoredEpochMs(startDateTime));
  const end = dayjs(parseStoredEpochMs(endDateTime));
  if (!start.isValid() || !end.isValid()) {
    return NaN;
  }
  return computeShiftDurationHours(start, end);
}

/** Display hours with two decimal places (e.g. shift range labels). */
export function formatDurationHours(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) {
    return '0.00';
  }
  return hours.toFixed(2);
}

/**
 * @param startDateTime unix timestamp string
 * @param endDateTime unix timestamp string
 * @returns diff in hours as a string (two decimal places)
 */
export const durationInHours = (startDateTime: string, endDateTime: string) => {
  return formatDurationHours(
    computeShiftDurationHoursFromUnixMs(startDateTime, endDateTime),
  );
};

/**
 * Get the duration in hours
 * @param startDate unix timestamp string
 * @param endDate unix timestamp string
 * @returns exmaple: ` (6h)`
 */
export const getDuration = (startDate: string, endDate: string) => {
  if (startDate && endDate) {
    const hours = computeShiftDurationHoursFromUnixMs(startDate, endDate);
    return ` (${formatDurationHours(hours)} hrs)`;
  }
  return '';
};

/**
 * Round to next 15-minute increment
 * @param date
 * @returns
 */
export const roundToNext15Minutes = (date: Date): Date => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 15;

  if (remainder === 0) {
    // Already on a 15-minute mark, add 15 minutes
    rounded.setMinutes(minutes + 15);
  } else {
    // Round up to next 15-minute mark
    rounded.setMinutes(minutes + (15 - remainder));
  }

  // Reset seconds and milliseconds
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);

  return rounded;
};

export const millisecondsToMinutes = (milliseconds: number) => {
  return milliseconds / 60000;
};

export const minutesToMilliseconds = (minutes: number) => {
  return minutes * 60000;
};

/**
 * Convert a time string (HH:mm) to Unix timestamp using a base date
 * @param timeString - Time in HH:mm format (e.g., "09:30")
 * @param baseDateTimestamp - Unix timestamp string to use as the base date
 * @returns Unix timestamp string
 */
export const timeStringToTimestamp = (
  timeString: string,
  baseDateTimestamp: string,
): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const baseDate = dayjs(Number(baseDateTimestamp));
  return baseDate
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0)
    .valueOf()
    .toString();
};
